import React from "react";
import "./ChanceRadar.css";

const COPY = {
  de: {
    kicker: "Chance Radar",
    first: "Erster Schritt",
    checked: "Quelle geprüft",
    linked: "Quelle verlinkt",
    footnote: "Nur Signale mit Datum und Quelle. Neuere stehen oben. Nichts davon ist geschätzt.",
    asOf: "Stand",
  },
  en: {
    kicker: "Chance Radar",
    first: "First step",
    checked: "Source checked",
    linked: "Source linked",
    footnote: "Only signals with a date and a source. Newer ones are listed first. Nothing here is estimated.",
    asOf: "As of",
  },
};

export const freshnessLabel = (iso, language, now = Date.now()) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ""))) return "";
  const [year, month, day] = iso.split("-").map(Number);
  const stamp = Date.UTC(year, month - 1, day);
  const today = new Date(now);
  const start = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const days = Math.round((start - stamp) / 86400000);
  const de = language !== "en";
  if (days <= 0) return de ? "Heute" : "Today";
  if (days === 1) return de ? "Gestern" : "Yesterday";
  if (days < 7) return de ? `vor ${days} Tagen` : `${days} days ago`;
  if (days < 14) return de ? "letzte Woche" : "Last week";
  if (days < 31) return de ? `vor ${Math.floor(days / 7)} Wochen` : `${Math.floor(days / 7)} weeks ago`;
  const months = Math.max(1, Math.floor(days / 30));
  return de ? `vor ${months} ${months === 1 ? "Monat" : "Monaten"}` : `${months} ${months === 1 ? "month" : "months"} ago`;
};

const asOf = (ms, language) => {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return "";
  return new Intl.DateTimeFormat(language === "en" ? "en-GB" : "de-DE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(n));
};

const safeHref = (url) => (/^https:\/\//i.test(String(url || "")) ? url : "");

const ChanceRadarView = ({ briefing }) => {
  if (!briefing) return null;
  const language = briefing.language === "en" ? "en" : "de";
  const copy = COPY[language];
  const chances = Array.isArray(briefing.chances) ? briefing.chances : [];
  const when = asOf(briefing.researchedAt, language);

  return (
    <article className="cr">
      <header className="cr-head">
        <p className="cr-kicker">{copy.kicker}</p>
        <h1>{briefing.title}</h1>
        {briefing.searched && <p className="cr-searched">{briefing.searched}</p>}
        {when && (
          <p className="cr-asof">
            {copy.asOf} {when}
          </p>
        )}
        {briefing.intro && <p className="cr-intro">{briefing.intro}</p>}
      </header>
      <ol className="cr-list">
        {chances.map((chance, index) => {
          const href = safeHref(chance.sourceUrl);
          return (
            <li key={`${chance.company}-${chance.signalDate}-${index}`}>
              <div className="cr-top">
                <span className="cr-rank">{index + 1}</span>
                <div className="cr-who">
                  <h2>{chance.company}</h2>
                  {chance.place && <p>{chance.place}</p>}
                </div>
                <span className="cr-when">{freshnessLabel(chance.signalDate, language)}</span>
              </div>
              <p className="cr-signal">{chance.signal}</p>
              <p className="cr-why">{chance.why}</p>
              <div className="cr-step">
                <span>{copy.first}</span>
                <p>{chance.firstStep}</p>
              </div>
              {chance.quote && <blockquote>{chance.quote}</blockquote>}
              {href && (
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {chance.sourceName || href}
                  <em>{chance.verified ? copy.checked : copy.linked}</em>
                </a>
              )}
            </li>
          );
        })}
      </ol>
      <p className="cr-foot">{copy.footnote}</p>
    </article>
  );
};

export default ChanceRadarView;
